import React, { useState, useEffect, useMemo } from 'react';
import { BuildingStorefrontIcon, ShieldCheckIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { StatsCards } from './ui';
import { classNames } from '../utils/classNames';

interface GlobalStats {
  totalShops: number;
  totalAdmins: number;
  totalOperators: number;
  activeAdmins: number;
}

interface AuditLog {
  id: number;
  ts: string;
  actor: string;
  action: string;
  meta: string;
}

const GlobalStats: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit] = useState(50);
  const [error, setError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState<{
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  } | null>(null);
  const [searchFilters, setSearchFilters] = useState({
    action: '',
    actor: '',
  });
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user?.role === 'owner') {
      fetchStats();
      fetchAuditLogs();
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'owner' && auditPage > 1) {
      fetchAuditLogs();
    }
  }, [auditPage]);

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setAuditPage(1);
      fetchAuditLogs();
    }, 400);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchFilters]);

  const fetchStats = async () => {
    try {
      setError(null);
      const response = await fetch('/api/owner/stats', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Ошибка загрузки статистики');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Ошибка сети при загрузке статистики');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const params = new URLSearchParams({
        limit: auditLimit.toString(),
        offset: ((auditPage - 1) * auditLimit).toString(),
      });

      if (searchFilters.action) {
        params.append('action', searchFilters.action);
      }
      if (searchFilters.actor) {
        params.append('actor', searchFilters.actor);
      }

      const response = await fetch(`/api/owner/audit?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs);
        setPaginationInfo({
          total: data.total,
          page: data.page,
          totalPages: data.totalPages,
          hasMore: data.hasMore,
        });
      } else {
        const errorData = await response.json();
        setAuditError(errorData.error || 'Ошибка загрузки логов аудита');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setAuditError('Ошибка сети при загрузке логов аудита');
    } finally {
      setAuditLoading(false);
    }
  };

  const statsCards = useMemo(() => {
    if (!stats) return [];
    return [
      {
        key: 'total-shops',
        title: 'Всего магазинов',
        amount: String(stats.totalShops ?? 0),
        icon: <BuildingStorefrontIcon className="h-6 w-6" />,
        variant: 'primary' as const,
      },
      {
        key: 'total-admins',
        title: 'Всего администраторов',
        amount: String(stats.totalAdmins ?? 0),
        icon: <UsersIcon className="h-6 w-6" />,
        variant: 'success' as const,
      },
      {
        key: 'total-operators',
        title: 'Всего операторов',
        amount: String(stats.totalOperators ?? 0),
        icon: <UserGroupIcon className="h-6 w-6" />,
        variant: 'warning' as const,
      },
      {
        key: 'active-admins',
        title: 'Активных администраторов',
        amount: String(stats.activeAdmins ?? 0),
        icon: <ShieldCheckIcon className="h-6 w-6" />,
        variant: 'neutral' as const,
      },
    ];
  }, [stats]);

  const formatAction = (action: string) => {
    const actionMap: Record<string, string> = {
      admin_created: 'Создан администратор',
      admin_activated: 'Администратор активирован',
      admin_deactivated: 'Администратор деактивирован',
      admin_password_reset: 'Сброс пароля администратора',
      provider_created: 'Создан провайдер',
      provider_updated: 'Провайдер обновлен',
      provider_deleted: 'Провайдер удален',
      shop_created: 'Создан магазин',
      api_key_rotated: 'API ключ изменен',
      webhook_test: 'Тест веб-хука',
      operator_created: 'Создан оператор',
      operator_activated: 'Оператор активирован',
      operator_deactivated: 'Оператор деактивирован',
      operator_deleted: 'Оператор удален',
    };

    return actionMap[action] || action;
  };

  const formatMeta = (meta: string) => {
    try {
      const parsed = JSON.parse(meta);
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    } catch {
      return meta;
    }
  };

  if (user?.role !== 'owner') {
    return <div className="flex min-h-[40vh] items-center justify-center text-danger">Доступ запрещен</div>;
  }

  if (loading) {
    return <div className="flex min-h-[40vh] items-center justify-center text-[var(--app-text-muted)]">Загрузка…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-[var(--app-heading)]">Глобальная статистика</h2>
        <p className="text-sm text-[var(--app-text-muted)]">
          Обзор ключевых показателей инфраструктуры и активностей пользователей.
        </p>
      </div>

      {error ? (
        <div className="rounded-2xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div>
      ) : null}

      {stats ? <StatsCards data={statsCards} /> : null}

      <section className="space-y-4">
        <header className="space-y-2">
          <h3 className="text-lg font-semibold text-[var(--app-heading)]">Аудит системы</h3>
          <p className="text-sm text-[var(--app-text-muted)]">История изменений конфигурации и действий пользователей.</p>
        </header>

        <div className="card">
          <div className="card-body space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="space-y-2 text-sm font-medium text-[var(--app-text)]">
                <span>Фильтр по действию</span>
                <input
                  type="text"
                  value={searchFilters.action}
                  onChange={(event) => setSearchFilters((prev) => ({ ...prev, action: event.target.value }))}
                  placeholder="Введите действие для поиска..."
                  className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-2 text-sm text-[var(--app-text)] placeholder:text-[var(--app-text-muted)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                />
              </label>
              <label className="space-y-2 text-sm font-medium text-[var(--app-text)]">
                <span>Фильтр по пользователю</span>
                <input
                  type="text"
                  value={searchFilters.actor}
                  onChange={(event) => setSearchFilters((prev) => ({ ...prev, actor: event.target.value }))}
                  placeholder="Введите имя пользователя..."
                  className="w-full rounded-xl border border-[var(--app-border)] bg-[var(--app-input-bg)] px-4 py-2 text-sm text-[var(--app-text)] placeholder:text-[var(--app-text-muted)] focus:border-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20"
                />
              </label>
            </div>

            {auditError ? (
              <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">{auditError}</div>
            ) : null}

            <div className="overflow-hidden rounded-2xl border border-[var(--app-border)]">
              <div className="grid grid-cols-2 gap-px bg-[var(--app-border)] text-xs font-semibold uppercase tracking-wide text-[var(--app-text-muted)] sm:grid-cols-4">
                <div className="bg-[var(--app-surface)] px-4 py-2">Действие</div>
                <div className="bg-[var(--app-surface)] px-4 py-2">Пользователь</div>
                <div className="hidden bg-[var(--app-surface)] px-4 py-2 sm:block">Детали</div>
                <div className="bg-[var(--app-surface)] px-4 py-2 text-right">Время</div>
              </div>
              <div className="divide-y divide-[var(--app-border)] bg-[var(--app-surface)]">
                {auditLoading ? (
                  <div className="px-4 py-6 text-sm text-[var(--app-text-muted)]">Загружаем логи…</div>
                ) : auditLogs.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-[var(--app-text-muted)]">Записей не найдено.</div>
                ) : (
                  auditLogs.map((log) => (
                    <div key={log.id} className="grid grid-cols-2 gap-y-2 px-4 py-4 text-sm sm:grid-cols-4 sm:items-center">
                      <div className="font-semibold text-[var(--app-heading)]">{formatAction(log.action)}</div>
                      <div className="text-[var(--app-text)]">
                        <p className="font-medium text-[var(--app-heading)]">{log.actor || '—'}</p>
                        <p className="text-xs text-[var(--app-text-muted)]">ID: {log.id}</p>
                      </div>
                      <div className="hidden text-sm text-[var(--app-text-muted)] sm:block">{formatMeta(log.meta)}</div>
                      <div className="text-right text-sm text-[var(--app-text-muted)]">
                        {new Date(log.ts).toLocaleString('ru-RU')}
                      </div>
                      <div className="col-span-2 text-xs text-[var(--app-text-muted)] sm:hidden">{formatMeta(log.meta)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {paginationInfo && paginationInfo.totalPages > 1 ? (
              <div className="flex items-center justify-between rounded-xl bg-[var(--app-surface-subtle)] px-4 py-3 text-sm text-[var(--app-text-muted)]">
                <div>
                  Страница {paginationInfo.page} из {paginationInfo.totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                    disabled={auditPage <= 1}
                    className={classNames(
                      'rounded-full px-3 py-1 text-xs font-semibold transition',
                      auditPage <= 1
                        ? 'cursor-not-allowed bg-[var(--app-surface)] text-[var(--app-text-muted)]/60'
                        : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                    )}
                  >
                    Назад
                  </button>
                  <button
                    onClick={() => setAuditPage((prev) => Math.min(paginationInfo.totalPages, prev + 1))}
                    disabled={auditPage >= paginationInfo.totalPages}
                    className={classNames(
                      'rounded-full px-3 py-1 text-xs font-semibold transition',
                      auditPage >= paginationInfo.totalPages
                        ? 'cursor-not-allowed bg-[var(--app-surface)] text-[var(--app-text-muted)]/60'
                        : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                    )}
                  >
                    Вперёд
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalStats;
